// A-7b PR 6c-3 — LLM styling-note fallback hook.
//
// Fires only when a confidence assessment carries `styling_pending=true`
// AND a `styling_llm_payload`. Calls the generate-styling-note edge function,
// validates the response client-side (defensive backstop — edge fn already
// validates), and exposes the styling copy so the card can append a sparkle
// row asynchronously.
//
// Non-blocking: card renders immediately. Hook resolves after 200-1500ms.
// Aborts on unmount or payload change.

import { useEffect, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { StylingLlmPayload } from "./guestReasoningTemplates";

const BANNED_TOKENS = [
  "hides", "hide", "minimizes", "minimize", "minimise", "minimises",
  "disguises", "disguise", "compensates", "compensate",
  "covers up", "cover up", "covers-up",
];

export function validateStylingCopyClient(s: unknown): string | null {
  if (typeof s !== "string") return null;
  const trimmed = s.trim();
  if (trimmed.length < 10 || trimmed.length > 130) return null;
  const lower = trimmed.toLowerCase();
  for (const banned of BANNED_TOKENS) {
    if (lower.includes(banned)) return null;
  }
  if (/^(you |try |wear |avoid |consider |pair )/i.test(trimmed)) return null;
  return trimmed;
}

export interface UseStylingNoteLLMArgs {
  enabled: boolean;
  payload: StylingLlmPayload | null | undefined;
  /** Stable cache key — re-runs the fetch when this changes. */
  cacheKey?: string | null;
}

export interface UseStylingNoteLLMResult {
  stylingCopy: string | null;
  loading: boolean;
}

export function useStylingNoteLLM({
  enabled,
  payload,
  cacheKey,
}: UseStylingNoteLLMArgs): UseStylingNoteLLMResult {
  const [stylingCopy, setStylingCopy] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const lastKeyRef = useRef<string | null>(null);

  useEffect(() => {
    if (!enabled || !payload) {
      setStylingCopy(null);
      setLoading(false);
      return;
    }
    const key = cacheKey ?? JSON.stringify(payload);
    if (lastKeyRef.current === key && stylingCopy != null) {
      // Already resolved for this payload.
      return;
    }
    lastKeyRef.current = key;

    let cancelled = false;
    const ctrl = new AbortController();
    // Defensive client-side timeout — the edge function also has its own.
    const timer = setTimeout(() => ctrl.abort(), 2500);
    setLoading(true);

    (async () => {
      try {
        const { data, error } = await supabase.functions.invoke(
          "generate-styling-note",
          { body: payload },
        );
        if (cancelled) return;
        if (error) {
          setStylingCopy(null);
          return;
        }
        const raw = (data as { styling_copy?: unknown } | null)?.styling_copy;
        setStylingCopy(validateStylingCopyClient(raw));
      } catch {
        if (!cancelled) setStylingCopy(null);
      } finally {
        clearTimeout(timer);
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
      ctrl.abort();
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, cacheKey]);

  return { stylingCopy, loading };
}
